import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils/cn'
import { Avatar } from '@/components/ui/Avatar'
import { VerifiedBadge } from '@/components/ui/VerifiedBadge'
import {
  IconHome, IconTrending, IconSearch, IconHash, IconBookmark,
  IconBell, IconUser, IconSettings, IconBarChart, IconUsers, IconShield, IconEdit
} from '@/components/ui/Icons'

interface SidebarProps {
  user: {
    id: string; username: string; display_name: string
    avatar_url: string | null; is_verified: boolean; karma_score: number; role: string
  } | null
  onClose: () => void
}

const mainNav = [
  { label: 'Feed', href: '/feed', icon: IconHome },
  { label: 'Trending', href: '/trending', icon: IconTrending },
  { label: 'Search', href: '/search', icon: IconSearch },
  { label: 'Topics', href: '/topics', icon: IconHash },
]

const userNav = [
  { label: 'Dashboard', href: '/dashboard', icon: IconBarChart },
  { label: 'Notifications', href: '/notifications', icon: IconBell },
  { label: 'Bookmarks', href: '/bookmarks', icon: IconBookmark },
  { label: 'Following', href: '/following', icon: IconUsers },
]

export function Sidebar({ user, onClose }: SidebarProps) {
  const location = useLocation()
  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(href + '/')

  const NavItem = ({ href, label, Icon }: { href: string; label: string; Icon: React.FC<{className?: string}> }) => (
    <Link to={href} onClick={onClose}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 relative group",
        isActive(href) ? "text-foreground" : "text-text-muted hover:bg-muted hover:text-foreground"
      )}>
      {isActive(href) && (
        <motion.div layoutId="sidebar-active"
          className="absolute inset-0 rounded-xl"
          style={{ background: "hsl(var(--sidebar-active))" }}
          transition={{ type: 'spring', damping: 30, stiffness: 400 }} />
      )}
      <Icon className={cn("w-5 h-5 relative z-10 flex-shrink-0", isActive(href) ? "text-brand" : "text-text-muted group-hover:text-foreground")} />
      <span className="relative z-10">{label}</span>
    </Link>
  )

  return (
    <nav className="flex flex-col h-full py-3 px-2">
      {/* Create button */}
      {user && (
        <div className="px-1 mb-4">
          <Link to="/post/create" onClick={onClose}
            className="btn-ig w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
            Create Post
          </Link>
        </div>
      )}

      {/* Main nav */}
      <div className="space-y-0.5 mb-4">
        {mainNav.map(({ label, href, icon: Icon }) => (
          <NavItem key={href} href={href} label={label} Icon={Icon} />
        ))}
      </div>

      {user && (
        <>
          <div className="h-px bg-border-subtle mx-2 mb-4" />
          <div className="space-y-0.5 mb-4">
            {userNav.map(({ label, href, icon: Icon }) => (
              <NavItem key={href} href={href} label={label} Icon={Icon} />
            ))}
            <NavItem href={"/profile/" + user.username} label="Profile" Icon={IconUser} />
          </div>
        </>
      )}

      {/* Wood/paper sidebar widget */}
      <div className="mt-auto px-1 mb-2">
        <div className="wood-card p-3 rounded-xl">
          <p className="section-wood-header text-xs mb-1">🪵 Community</p>
          <p className="text-text-muted text-xs leading-relaxed">
            Share knowledge, spark discussion, build community.
          </p>
          {!user && (
            <Link to="/auth/register" onClick={onClose}
              className="mt-2 btn-ig w-full block text-center text-xs py-1.5 rounded-lg font-medium">
              Join Squids
            </Link>
          )}
        </div>
      </div>

      {/* Bottom nav */}
      <div className="px-1 space-y-0.5">
        <NavItem href="/settings" label="Settings" Icon={IconSettings} />
        {user?.role === 'admin' && <NavItem href="/admin" label="Admin" Icon={IconShield} />}
      </div>
    </nav>
  )
}